import { use, useCallback, useEffect, useRef } from "react";

import { useIsLoggedIn } from "@/components/shared/is-logged-in";
import { usePostCacheContext } from "@/contexts/post-cache-context";
import { UserContext } from "@/contexts/user-context";
import { hasRecentlySentPostView } from "@/utils/post-view-cache";
import { queuePostView } from "@/utils/post-view-queue";

type TrackViewDTO = {
  postHash: string;
  delay: number;
  postCachePrefix?: string | boolean;
};

export const useTrackViews = ({
  postHash,
  delay,
  postCachePrefix,
}: TrackViewDTO) => {
  const {
    viewIntersectionObserver,
    viewIntersecting,
    viewTracked,
    postCacheSignal,
    postCacheKey,
  } = usePostCacheContext(postHash, postCachePrefix);
  const { currentUserPublicKey } = use(UserContext);
  const timeoutRef = useRef<NodeJS.Timeout>(null!);
  const ref = useRef<HTMLDivElement>(null!);
  const isLoggedIn = useIsLoggedIn();

  const markViewTracked = useCallback(() => {
    postCacheSignal.value = {
      ...postCacheSignal.value,
      viewTracked: true,
    };
  }, [postCacheSignal]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.setAttribute("data-cache-key", postCacheKey);
    viewIntersectionObserver.observe(ref.current);

    return () => viewIntersectionObserver.unobserve(node);
  }, [postCacheKey, viewIntersectionObserver]);

  useEffect(() => {
    if (!currentUserPublicKey || viewTracked) {
      return;
    }

    if (
      hasRecentlySentPostView({
        postHash,
        publicKey: currentUserPublicKey,
      })
    ) {
      markViewTracked();
    }
  }, [currentUserPublicKey, markViewTracked, postHash, viewTracked]);

  useEffect(() => {
    const publicKey = currentUserPublicKey;

    if (!viewIntersecting || viewTracked || !isLoggedIn || !publicKey) {
      clearTimeout(timeoutRef.current);
      return;
    }

    const startIntersecting = Date.now();
    timeoutRef.current = setTimeout(() => {
      if (
        startIntersecting &&
        Date.now() - startIntersecting >= delay &&
        !viewTracked
      ) {
        const viewQueued = queuePostView({
          postHash,
          publicKey,
        });

        if (viewQueued) {
          markViewTracked();
        }
      }
    }, delay);

    return () => clearTimeout(timeoutRef.current);
  }, [
    currentUserPublicKey,
    delay,
    isLoggedIn,
    markViewTracked,
    postHash,
    viewIntersecting,
    viewIntersectionObserver,
    viewTracked,
  ]);

  return {
    ref,
  };
};
